/**
 * Creates/returns a shared dialog element to use as a "lightbox". The dialog is a browser native
 * element now, so we can just set content and cycle through images as needed. Don't need any
 * other third party dependencies or support, and we can more easily tailor to meet our needs.
 *
 * @return {HTMLDialogElement} The dialog.
 */
function getDialog() {
    // We track the dialog on the function for a shared single instance, in case the behaviour
    // ever applies to multiple galleries within the context of a single page.
    if (getDialog._$dialog) {
        return getDialog._$dialog;
    }

    getDialog._$dialog = document.createElement('dialog');
    getDialog._$dialog.classList.add('agend-gallery__dialog');

    // Add nav buttons, a close button, and an empty image + description/etc.
    getDialog._$dialog.innerHTML = `
        <div class="agend-gallery__dialog-backdrop">
        </div>
        <div class="agend-gallery__dialog-content">
            <nav class="agend-gallery__dialog-navigation">
                <button class="agend-gallery__dialog-button agend-gallery__dialog-button--prev">Prev</button>
                <button class="agend-gallery__dialog-button agend-gallery__dialog-button--next">Next</button>
                <button class="agend-gallery__dialog-button agend-gallery__dialog-button--close">Close</button>
            </nav>
            <div class="agend-gallery__dialog-image-wrapper">
                <img class="agend-gallery__dialog-image" src="" alt="" />
                <strong class="agend-gallery__dialog-image-title"></strong>
                <span class="agend-gallery__dialog-image-description"></span>
            </div>
        </div>
    `;

    // For the buttons, if there is a target activator, proxy the click to it.
    getDialog._$dialog.querySelectorAll('button').forEach($button => {
        $button.addEventListener('click', event => {
            // If we have an activator, click it.
            if ($button._$galleryActivator) {
                $button._$galleryActivator.click();
            }

            // If we are the close button, close the dialog.
            if ($button.matches('.agend-gallery__dialog-button--close')) {
                getDialog._$dialog.close();
            }
        });
    });

    // Close the diag when clicked on BG.
    getDialog._$dialog.addEventListener('click', event => {
        if (event.target === getDialog._$dialog) {
            getDialog._$dialog.close();
        }
    });

    // And finally add the dialog to the page.
    document.body.append(getDialog._$dialog);
}

/**
 * Given a URL, open the dialog (if closed) and display the image. If prev/next activators (ie, the link to
 * click on another image) are provided the buttons will proxy clicks to those.
 *
 * @param {HTMLElement} $link
 * @param {HTMLElement} $previousImageActivator
 * @param {HTMLElement} $nextImageActivator
 */
function setCurrentImage($link, $previousImageActivator = null, $nextImageActivator = null) {
    const $dialog = getDialog();

    const url = $link.getAttribute('href');
    const title = $link.querySelector('picture')?.getAttribute('title')
        || $link.querySelector('img')?.getAttribute('title')
        || '';
    const description = $link.querySelector('img')?.getAttribute('alt') || '';

    $dialog.querySelector('img').src = url;
    $dialog.querySelector('.agend-gallery__dialog-image-title').innerText = title;
    $dialog.querySelector('.agend-gallery__dialog-image-description').innerText = description;

    // Disable buttons by default, and remove activator targets.
    $dialog.querySelectorAll('button:not(.agend-gallery__dialog-button--close)').forEach($button => {
        $button.disabled = true;
        $button._$galleryActivator = null;
    });

    // If we have a previous image, enable the button and proxy clicks to the activator.
    if ($previousImageActivator) {
        const $button = $dialog.querySelector('button.agend-gallery__dialog-button--prev');
        $button.disabled = false;
        $button._$galleryActivator = $previousImageActivator;
    }

    // If we have a next image, enable the button and proxy clicks to the activator.
    if ($nextImageActivator) {
        const $button = $dialog.querySelector('button.agend-gallery__dialog-button--next');
        $button.disabled = false;
        $button._$galleryActivator = $nextImageActivator;
    }

    if (!$dialog.open) {
        $dialog.showModal();
    }
}

function setupGallery($candidate) {

    // Trap clicks...
    $candidate.addEventListener('click', event => {
        // bail if not clicked on an image.
        if (event.target.tagName !== 'IMG') {
            return;
        }

        // bail if we can't get the URL from the parent a tag.
        const $link = event.target.closest('a');
        if (!$link || !$link?.getAttribute('href')) {
            return;
        }

        event.stopImmediatePropagation();
        event.preventDefault();

        // Set the current image, with previous and next. This approach means we can use a single
        // dialog with multiple galleries in the page (not that that will ever happen...).
        setCurrentImage(
            $link,
            event.target.closest('li').previousElementSibling?.querySelector('img'),
            event.target.closest('li').nextElementSibling?.querySelector('img'),
        );

        return false;
    });


}

document.addEventListener('DOMContentLoaded', () => {

    const $candidates = document.querySelectorAll('.agend-gallery');

    if ($candidates.length === 0) {
        return;
    }

    // Setup the dialog.
    getDialog();

    // Setup each gallery.
    $candidates.forEach(setupGallery);

});
